import React, { useState, useEffect } from 'react'
import {
  Table,
  Button,
  Space,
  Card,
  Form,
  Input,
  Modal,
  Select,
  Tag,
  Popconfirm,
  Avatar,
  Switch,
  App,
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  SearchOutlined,
  LockOutlined,
} from '@ant-design/icons'
import { authAPI } from '../services/api'

const { Option } = Select

interface User {
  id: string
  username: string
  email: string
  roles: string[]
  isActive: boolean
  lastLogin?: string
  createdAt: string
}

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(false)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [isPasswordModalVisible, setIsPasswordModalVisible] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [selectedUserForPassword, setSelectedUserForPassword] = useState<User | null>(null)
  const [form] = Form.useForm()
  const [passwordForm] = Form.useForm()
  const { message } = App.useApp()

  // Load users from API
  useEffect(() => {
    loadUsers()
  }, [])

  const loadUsers = async () => {
    try {
      setLoading(true)
      const response = await authAPI.getAllUsers()
      setUsers(response.data)
    } catch (error) {
      console.error('Error loading users:', error)
      message.error('Failed to load users')
    } finally {
      setLoading(false)
    }
  }

  const handleAdd = () => {
    setEditingUser(null)
    form.resetFields()
    setIsModalVisible(true)
  }

  const handleEdit = (user: User) => {
    setEditingUser(user)
    form.setFieldsValue(user)
    setIsModalVisible(true)
  }

  const handleResetPassword = (user: User) => {
    setSelectedUserForPassword(user)
    passwordForm.resetFields()
    setIsPasswordModalVisible(true)
  }

  const handleDelete = async (id: string) => {
    try {
      await authAPI.deleteUser(id)
      message.success('User deleted successfully')
      loadUsers()
    } catch (error) {
      console.error('Error deleting user:', error)
      message.error('Failed to delete user')
    }
  }

  const handleSubmit = async (values: any) => {
    try {
      if (editingUser) {
        // Update existing user
        await authAPI.updateUser(editingUser.id, values)
        message.success('User updated successfully')
      } else {
        // Create new user
        const response = await authAPI.createUser(values)
        message.success('User created successfully')
        
        // If isActive is false, update the user status separately
        if (values.isActive === false && response.data?.user?.id) {
          try {
            await authAPI.updateUserStatus(response.data.user.id, false)
          } catch (statusError) {
            console.warn('Could not set initial user status:', statusError)
          }
        }
      }
      
      setIsModalVisible(false)
      form.resetFields()
      loadUsers()
    } catch (error) {
      console.error('Error saving user:', error)
      message.error('Failed to save user')
    }
  }

  const handlePasswordSubmit = async (values: any) => {
    if (!selectedUserForPassword) return

    try {
      await authAPI.resetUserPassword(selectedUserForPassword.id, values.newPassword)
      message.success(`Password reset for ${selectedUserForPassword.username} successfully`)
      setIsPasswordModalVisible(false)
      passwordForm.resetFields()
      setSelectedUserForPassword(null)
      loadUsers()
    } catch (error) {
      console.error('Error resetting password:', error)
      message.error('Failed to reset password')
    }
  }

  const handleStatusChange = async (id: string, isActive: boolean) => {
    try {
      await authAPI.updateUserStatus(id, isActive)
      message.success('User status updated successfully')
      loadUsers()
    } catch (error) {
      console.error('Error updating user status:', error)
      message.error('Failed to update user status')
    }
  }

  const columns = [
    {
      title: 'Avatar',
      dataIndex: 'avatar',
      key: 'avatar',
      render: (_: any, record: User) => (
        <Avatar
          size="large"
          icon={<UserOutlined />}
          style={{ backgroundColor: '#87d068' }}
        >
          {record.username.charAt(0).toUpperCase()}
        </Avatar>
      ),
    },
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder="Search username"
            value={selectedKeys[0]}
            onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Search
            </Button>
            <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>
              Reset
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered: boolean) => (
        <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
      ),
      onFilter: (value: any, record: User) =>
        record.username.toLowerCase().includes(value.toLowerCase()),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Roles',
      dataIndex: 'roles',
      key: 'roles',
      render: (roles: string[]) => (
        <div>
          {roles.map(role => (
            <Tag 
              key={role} 
              color={
                role === 'ADMIN' ? 'red' : 
                role === 'CREATOR' ? 'green' : 
                'blue'
              }
            >
              {role}
            </Tag>
          ))}
        </div>
      ),
      filters: [
        { text: 'Admin', value: 'ADMIN' },
        { text: 'Player', value: 'PLAYER' },
        { text: 'Creator', value: 'CREATOR' },
      ],
      onFilter: (value: any, record: User) => record.roles.includes(value),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean, record: User) => (
        <Switch
          checked={isActive}
          onChange={(checked) => handleStatusChange(record.id, checked)}
          checkedChildren="Active"
          unCheckedChildren="Inactive"
        />
      ),
    },
    {
      title: 'Last Login',
      dataIndex: 'lastLogin',
      key: 'lastLogin',
      render: (date: string) => date ? new Date(date).toLocaleString() : 'Never',
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: (a: User, b: User) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: User) => (
        <Space size="middle">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Button
            type="link"
            icon={<LockOutlined />}
            onClick={() => handleResetPassword(record)}
          >
            Reset Password
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this user?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <div>
      <Card>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
          <h2>Users Management</h2>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            Add User
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          pagination={{
            total: users.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          }}
        />
      </Card>

      <Modal
        title={editingUser ? 'Edit User' : 'Add User'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false)
          form.resetFields()
        }}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="username"
            label="Username"
            rules={[
              { required: true, message: 'Please enter username' },
              { min: 3, message: 'Username must be at least 3 characters' },
            ]}
          >
            <Input placeholder="Enter username" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter a valid email' },
            ]}
          >
            <Input placeholder="Enter email" />
          </Form.Item>

          {!editingUser && (
            <Form.Item
              name="password"
              label="Password"
              rules={[
                { required: true, message: 'Please enter password' },
                { min: 6, message: 'Password must be at least 6 characters' },
              ]}
            >
              <Input.Password placeholder="Enter password" />
            </Form.Item>
          )}

          <Form.Item
            name="roles"
            label="Roles"
            rules={[{ required: true, message: 'Please select at least one role' }]}
          >
            <Select 
              mode="multiple" 
              placeholder="Select roles"
              allowClear
            >
              <Option value="ADMIN">Admin</Option>
              <Option value="PLAYER">Player</Option>
              <Option value="CREATOR">Creator</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="isActive"
            label="Status"
            initialValue={true}
            valuePropName="checked"
          >
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setIsModalVisible(false)
                form.resetFields()
              }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingUser ? 'Update' : 'Create'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`Reset Password for ${selectedUserForPassword?.username}`}
        open={isPasswordModalVisible}
        onCancel={() => {
          setIsPasswordModalVisible(false)
          passwordForm.resetFields()
          setSelectedUserForPassword(null)
        }}
        footer={null}
        width={500}
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handlePasswordSubmit}
        >
          <Form.Item
            name="newPassword"
            label="New Password"
            rules={[
              { required: true, message: 'Please enter new password' },
              { min: 6, message: 'Password must be at least 6 characters' },
            ]}
          >
            <Input.Password placeholder="Enter new password" />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="Confirm Password"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Please confirm password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve()
                  }
                  return Promise.reject(new Error('Passwords do not match'))
                },
              }),
            ]}
          >
            <Input.Password placeholder="Confirm password" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => {
                setIsPasswordModalVisible(false)
                passwordForm.resetFields()
                setSelectedUserForPassword(null)
              }}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" danger>
                Reset Password
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default Users
